import { ElementRef, useCallback, useRef, useState } from 'react';
import {
  BonanzaLogo,
  Box,
  Button,
  Input,
  Text,
  KeyboardSpacer,
  PressableIcon,
  Checkbox,
} from '@bonanza-social/moonlight';
import {
  BottomSheet,
  Modal,
  StackNavigationProp,
  useNavigation,
} from '@bonanza-social/navigation';
import { useTranslation } from '@bonanza-social/i18n';
import { registerUser, useDispatch } from '@bonanza-social/toolkit/state';
import { RegistrationData, submitSocialRegistrationForm } from '../../../state';
import { ApiError } from '@bonanza-social/toolkit/api';
import { ERROR_BOTTOM_SHEETS } from '../../../components/Registration/Registration.constants';

function SocialRegistrationModal() {
  const navigation =
    useNavigation<StackNavigationProp<AuthNavigatorParamsList>>();

  const dispatch = useDispatch();
  const emailRef = useRef<ElementRef<typeof Input>>(null);
  const passwordRef = useRef<ElementRef<typeof Input>>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showError, setShowError] = useState(false);
  const [marketing, setMarketing] = useState(true);
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isFormValid = !!email && !!password;
  const isButtonDisabled = !isFormValid || isLoading;

  const { t } = useTranslation('registration');

  const onEmailSubmit = useCallback(() => {
    passwordRef.current?.focus();
  }, []);

  const onRegisterPress = useCallback(async () => {
    setIsLoading(true);

    if (!email || !password) {
      if (!showError) {
        setShowError(true);
        setFormError(t('missingFields'));
      }

      return;
    }

    if (formError) {
      setFormError('');
    }

    const data = {
      email,
      password,
      marketing,
    } as RegistrationData;

    const actionResult = await dispatch(submitSocialRegistrationForm(data));

    if (submitSocialRegistrationForm.fulfilled.match(actionResult)) {
      const result = actionResult.payload;
      const { codeVerifier, authorizationCode } = result.account;

      dispatch(registerUser({ codeVerifier, authorizationCode }));

      setIsLoading(false);

      Modal.close();

      return;
    }

    if (!submitSocialRegistrationForm.rejected.match(actionResult)) {
      setIsLoading(false);
      return;
    }

    const error = actionResult.payload;

    setIsLoading(false);

    if (!(error instanceof ApiError)) return;

    const { errorCode } = error?.data || {};
    if (!errorCode) return;

    const bottomSheetConfig =
      errorCode in ERROR_BOTTOM_SHEETS
        ? ERROR_BOTTOM_SHEETS[errorCode as keyof typeof ERROR_BOTTOM_SHEETS]
        : null;

    if (bottomSheetConfig) {
      BottomSheet.open(bottomSheetConfig);
    } else {
      BottomSheet.open({
        namespace: 'common',
        type: 'Info',
        data: {
          textNS: 'registration',
          textKey: 'defaultErrorBottomSheetText',
        },
      });
    }
  }, [email, password, formError, marketing, dispatch, showError, t]);

  const onLoginPress = useCallback(() => {
    Modal.close();
    navigation.navigate('LoginScreen');
  }, [navigation]);

  return (
    <Box flex={1} justifyContent="space-between" padding="space-200">
      <Box gap="space-600">
        <Box>
          <PressableIcon
            name="close"
            width="space-600"
            height="space-600"
            gradient="accentBackground"
            alignSelf="flex-end"
            onPress={() => Modal.close()}
            testID="modal-header-close"
          />
          <BonanzaLogo marginTop="space-300" />
        </Box>
        <Box gap="space-600" padding="space-200">
          <Input
            ref={emailRef}
            placeholderKey="email"
            placeholderNS="auth"
            autoComplete="username"
            keyboardType="email-address"
            textContentType="emailAddress"
            autoCapitalize="none"
            autoCorrect={false}
            value={email}
            onChangeText={setEmail}
            enterKeyHint="next"
            onSubmitEditing={onEmailSubmit}
            onFocus={() => setShowError(false)}
            importantForAutofill="yes"
            error={formError}
          />
          <Box flexDirection="row" gap="space-300">
            <Input
              ref={passwordRef}
              placeholderKey="password"
              placeholderNS="auth"
              secureTextEntry
              autoComplete="current-password"
              textContentType="password"
              value={password}
              autoCorrect={false}
              autoCapitalize="none"
              onChangeText={setPassword}
              onSubmitEditing={() => onRegisterPress()}
              enterKeyHint="go"
              importantForAutofill="yes"
              flex={1}
              error={formError}
            />
          </Box>
          {formError ? (
            <Text color="darkSurfaceError" variant="body-m">
              {formError}
            </Text>
          ) : null}
          <Box flexDirection="row" gap="space-300" alignItems="center">
            <Checkbox checked={marketing} onChange={setMarketing} />
            <Text
              variant="body-s"
              color="darkSurfacePrimary"
              locNS="registration"
              locKey="marketingLabel"
            />
          </Box>
        </Box>
      </Box>
      <Box padding="space-200">
        <Button
          size="large"
          locKey="ctaComplete"
          locNS="registration"
          onPress={() => onRegisterPress()}
          disabled={isButtonDisabled}
          loading={isLoading}
        />
        <Box
          justifyContent="center"
          flexDirection="row"
          gap="space-050"
          paddingTop={'space-600'}
        >
          <Text
            variant="body-s-semi"
            color="darkSurfacePrimary"
            locNS="auth"
            locKey="alreadyHaveAccount"
          />
          <Text
            variant="link-small"
            color="darkSurfacePrimary"
            onPress={() => onLoginPress()}
            locNS="auth"
            locKey="login"
          />
        </Box>
        <KeyboardSpacer isInsideSafeAreaView additionalOffset={-30} />
      </Box>
    </Box>
  );
}

export default SocialRegistrationModal;
